import PropTypes from 'prop-types';

import { useAppSelector } from 'dux/app/hooks';

import isEmpty from 'lodash/isEmpty';

import { legacyTheme, styled } from '@prose-ui/legacy';

import Button from 'Components/LegacyButton';
import Spacer from 'Components/Spacer';
import TicketLineV2 from 'Components/TicketLineV2';
import Typography from 'Components/Typography';

import { countriesCode } from 'constants/countries';

import TicketDiscountsBlockV2 from 'Blocks/TicketDiscountsV2';

import { plural } from 'utils/textUtils';
import useResponsiveVariant from 'utils/useResponsiveVariant';

import {
  getCartCreditCouponsV2,
  getCartOfferCouponsV2,
  getCartV2Currency,
  getCartV2Data,
  getCartV2IsFree,
  getComputedCartV2Items,
  getItemQuantityInCartV2,
} from 'dux/cartV2/selectors';
import * as checkoutCartSelectors from 'dux/checkoutCart/selectors';
import { getProductImageForProduct } from 'dux/products/selectors';

const TicketBorder = styled.hr`
  margin: 16px 0;
  border: none;
  border-top: 1px solid ${legacyTheme.palette.common.grey.light};

  ${legacyTheme.breakpoints.up('sm')} {
    margin: 24px 0;
  }
`;

const ProductsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: ${({ hasPaddingTop }) => hasPaddingTop && '16px'};
`;

const TotalPricesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CTAInTicket = styled(Button)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const SatisfactionGuaranteeMessage = styled(Typography)`
  font-size: 10px;
`;

const CartOrderSummaryV2 = ({ ctaButtonProps, isCheckout }) => {
  const { isMobile } = useResponsiveVariant();

  const isNewCheckoutAccordion = isCheckout && isMobile;

  const cart = useAppSelector(getCartV2Data);

  const getProductImage = useAppSelector(getProductImageForProduct);

  const currency = useAppSelector(getCartV2Currency);
  const freeOrder = useAppSelector(getCartV2IsFree);
  const creditCoupons = useAppSelector(getCartCreditCouponsV2);
  const offerCoupons = useAppSelector(getCartOfferCouponsV2);

  const consultationProfileCountry = useAppSelector(
    checkoutCartSelectors.getConsultationProfileCountryInCartSurvey
  );

  const cartProducts = useAppSelector(getComputedCartV2Items);
  const cartProductsQuantity = useAppSelector(getItemQuantityInCartV2);

  const isTaxComputed = isCheckout && (!cart.need_address || cart.shipping_address);
  const total = isCheckout ? cart.total_price_with_taxes : cart.total_price + cart.total_shipping;
  const totalAmount = cart.total_amount;
  const totalShipping = cart.total_shipping;
  const totalTaxes = isCheckout ? cart.total_taxes : null;

  return (
    <>
      <ProductsContainer
        data-testid="gift-with-purchase-cart-summary"
        hasPaddingTop={isNewCheckoutAccordion}
      >
        {Boolean(cartProducts?.length) && (
          <>
            {cartProducts.map(product => {
              const productImage = isCheckout
                ? getProductImage({
                    product,
                    noPumps: true,
                    mustHaveJars: false,
                  })
                : null;
              const productLabel = `(${product.quantity})\u00a0${product.label}`;
              const productKey = `${product.variant_slug || product.product_type}-${
                product.category
              }`;
              return (
                <TicketLineV2
                  key={`${productKey}-ticket-line`}
                  currency={currency}
                  isSubscriptionDiscount={false}
                  label={productLabel}
                  price={product.total_price}
                  priceDisplay={product.total_price_display}
                  productKey={productKey}
                  sideImage={productImage}
                />
              );
            })}
            <TicketBorder />
          </>
        )}
      </ProductsContainer>

      <TotalPricesContainer>
        {/* Used on order history and cart success */}
        <TicketLineV2
          currency={currency}
          label={`Subtotal (${plural(cartProductsQuantity, true, 'item')})`}
          price={totalAmount}
        />
        {(!isEmpty(offerCoupons) || !isEmpty(creditCoupons)) && (
          <TicketDiscountsBlockV2
            creditCoupons={creditCoupons}
            currency={currency}
            hasSubscription={false}
            offerCoupons={offerCoupons}
          />
        )}
        <TicketLineV2
          currency={currency}
          label="shipping"
          price={totalShipping === 0.0 ? 'Free' : totalShipping}
        />
        {isTaxComputed && <TicketLineV2 currency={currency} label="Tax" price={totalTaxes} />}
        <TicketLineV2
          currency={currency}
          dataTestId="checkout-total-ticket-line-v2"
          id="ticket-line-total"
          isBold
          label={isTaxComputed ? 'Total' : 'Total (pre-tax)'}
          price={freeOrder ? 'Free' : total}
        />
      </TotalPricesContainer>
      <Spacer size={24} />
      {!isCheckout && !isMobile && ctaButtonProps && (
        <>
          <CTAInTicket
            data-testid="checkout-ticket-cta"
            disabled={ctaButtonProps.disabled}
            isLoading={ctaButtonProps.isLoading}
            noMargin
            onClick={ctaButtonProps.onClick}
            text={ctaButtonProps.text}
            type={ctaButtonProps.type}
            variant={ctaButtonProps.variant}
          >
            {ctaButtonProps.text}
          </CTAInTicket>
          <Spacer size={24} />
        </>
      )}
      {consultationProfileCountry === countriesCode.CA && (
        <>
          <Typography align="center" color="grey" variant="p3">
            No additional duties and taxes collected upon delivery.
          </Typography>
          <Spacer size={24} />
        </>
      )}
      {!isCheckout && (
        <>
          <SatisfactionGuaranteeMessage align="center" color="grey" variant="mono3">
            100% satisfaction guarantee
          </SatisfactionGuaranteeMessage>
          <Spacer size={40} />
        </>
      )}
    </>
  );
};

CartOrderSummaryV2.propTypes = {
  ctaButtonProps: PropTypes.shape({
    text: PropTypes.string,
    disabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    onClick: PropTypes.func,
    type: PropTypes.string,
    variant: PropTypes.string,
  }),
  isCheckout: PropTypes.bool,
};

CartOrderSummaryV2.defaultProps = {
  ctaButtonProps: null,
  isCheckout: false,
};

export default CartOrderSummaryV2;
